import { Intent } from "@blueprintjs/core";
import { Breadcrumbs, VerticalLinkGroup } from "components/navigation";
import { NotificationToaster } from "components/notifications";
import { AuthenticatedRoute, UserAccess, UserHasAccess } from "components/user-access";
import { PageRestricted } from "pages/errors/page-restricted";
import { EventTypeChangelog } from "pages/fleet/fleet-event-type/edit/changelog/EventTypeChangelog";
import { EventTypeChecks, EventTypeCost, EventTypeDetails, EventTypeIcon, EventTypeProcess, EventTypeSchedule, EventTypeSettings } from "pages/fleet/fleet-settings";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Route, Routes, Navigate, useParams } from "react-router-dom";
import { 
    DefaultEventTypeChecksModel, DefaultEventTypeCostsModel, DefaultEventTypeDetailsModel, 
    DefaultEventTypeIconModel, DefaultEventTypeProcessModel, DefaultEventTypeScheduleModel, 
    DefaultEventTypeSettingsModel, FailedOnlyCommentOption, FailedOnlyCostOption,
    FailedOnlySignoffOption, FailedVorRequiredOption, FailedOnlyDefectOption
} from "pages/fleet/fleet-event-type/wizard/shared/DefaultEventTypeModel";
import { EventTypeHelpers } from "pages/fleet/fleet-event-type/wizard/shared/EventTypeHelpers";
import { FleetService } from "services";

export function EventTypeEdit({
    breadcrumbs
}) {
    const { id } = useParams();
    const requiredActions = ["FMS:Manage"];
    const checksAction = "Checks:Manage";

    const userHasChecksAction = UserHasAccess(checksAction);

    const [currentEventType, setCurrentEventType] = useState("Event Type");

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [forceLoadChildren, setForceLoadChildren] = useState(false);
    const [specialProcessingKey, setSpecialProcessingKey] = useState(null);
    const [isErMaintenanceLocked, setIsErMaintenanceLocked] = useState(false);

    const [eventTypeDetailsModel, setEventTypeDetailsModel] = useState(DefaultEventTypeDetailsModel);
    const [eventTypeIconModel, setEventTypeIconModel] = useState(DefaultEventTypeIconModel);
    const [eventTypeProcessModel, setEventTypeProcessModel] = useState(DefaultEventTypeProcessModel);
    const [eventTypeSettingsModel, setEventTypeSettingsModel] = useState(DefaultEventTypeSettingsModel);
    const [eventTypeScheduleModel, setEventTypeScheduleModel] = useState(DefaultEventTypeScheduleModel);
    const [eventTypeCostModel, setEventTypeCostModel] = useState(DefaultEventTypeCostsModel);
    const [eventTypeChecksModel, setEventTypeChecksModel] = useState(DefaultEventTypeChecksModel);

    const eventTypeDetailsStep = 0;
    const eventTypeIconStep = 1;
    const eventTypeProcessStep = 2;
    const eventTypeSettingsStep = 3;
    const eventTypeScheduleStep = 4;
    const eventTypeCostStep = 5;
    const eventTypeChecksStep = 6;

    const routes = useMemo(getRoutes, [eventTypeDetailsModel.adHoc])

    function getRoutes() {
        const internalRoutes = [
            { location: `/fleet-settings/event-types/${id}/details`, title: "Details" },
            { location: `/fleet-settings/event-types/${id}/icon`, title: "Icon" },
            { location: `/fleet-settings/event-types/${id}/process`, title: "Process" },
            { location: `/fleet-settings/event-types/${id}/settings`, title: "Settings" },
            { location: `/fleet-settings/event-types/${id}/schedule`, title: "Schedule" },
            { location: `/fleet-settings/event-types/${id}/cost`, title: "Costs" },
            { location: `/fleet-settings/event-types/${id}/history`, title: "History" },
        ];

        if (userHasChecksAction) {
            internalRoutes.splice(eventTypeChecksStep, 0, { location: `/fleet-settings/event-types/${id}/checks`, title: "Checks" });
        }

        return internalRoutes;
    }

    useEffect(findCurrentRoute, [eventTypeDetailsModel.name]);

    function findCurrentRoute() {
        const eventTypeName = eventTypeDetailsModel.name === null || eventTypeDetailsModel.name === undefined || eventTypeDetailsModel.name.trim() === ""
            ? "Event Type"
            : eventTypeDetailsModel.name;

        setCurrentEventType(eventTypeName);
    }

    useEffect(loadEventType, [])

    function loadEventType() {
        setLoading(true);

        FleetService.getEventType(id)
            .then((response) => {
                const isErLocked = response.isErMaintenanceSendingEnabled === true &&
                    response.specialProcessingKey?.toUpperCase() === 'PMI';

                setSpecialProcessingKey(response.specialProcessingKey);
                setIsErMaintenanceLocked(isErLocked);

                const constructedModels = EventTypeHelpers.constructAndSetModelFromResponse(
                    response,
                    eventTypeDetailsModel,
                    eventTypeIconModel,
                    eventTypeProcessModel,
                    eventTypeSettingsModel,
                    eventTypeScheduleModel,
                    eventTypeCostModel,
                    eventTypeChecksModel
                );

                let lockedProcessModel = constructedModels.process;
                let lockedSettingsModel = constructedModels.settings;

                if (isErLocked) {
                    // ER maintenance accounts enforce a fixed PMI signoff/defect configuration server-side;
                    // force those values here so the locked fields and the save payload reflect the
                    // compliant state rather than the stored event type's values.
                    const lockedModels = EventTypeHelpers.applyErMaintenanceLockedValues(constructedModels.process, constructedModels.settings);
                    lockedProcessModel = lockedModels.process;
                    lockedSettingsModel = lockedModels.settings;
                }

                setEventTypeDetailsModel(prev => { return {...prev, ...constructedModels.details}; });
                setEventTypeIconModel(prev => { return {...prev, ...constructedModels.icons}; });
                setEventTypeProcessModel(prev => { return {...prev, ...lockedProcessModel}; });
                setEventTypeSettingsModel(prev => { return {...prev, ...lockedSettingsModel}; });
                setEventTypeScheduleModel(prev => { return {...prev, ...constructedModels.schedule}; });
                setEventTypeCostModel(prev => { return {...prev, ...constructedModels.costs}; });
                setEventTypeChecksModel(prev => { return {...prev, ...constructedModels.checks}; });
                setForceLoadChildren(true);
            })
            .catch(() => {
                NotificationToaster.show(Intent.WARNING, 'Event type could not be loaded. Please try again later.');
            })
            .finally(() => {
                setLoading(false);
            });
    }

    function onModelUpdated(step, model) {
        switch (step) {
            case eventTypeDetailsStep: {
                setEventTypeDetailsModel(model);
                break;
            }
            case eventTypeIconStep: {
                setEventTypeIconModel(model);
                break;
            }
            case eventTypeProcessStep: {
                setEventTypeProcessModel(model);

                // Models only need to be cleaned up if fail status is disabled
                if (model.failStatusEnabled === true) {
                    return;
                }

                const cleanedUpModels = EventTypeHelpers.cleanupModelsBasedOnProcessModelChanges(
                    model,
                    eventTypeSettingsModel,
                    eventTypeScheduleModel,
                    eventTypeCostModel
                );
                
                setEventTypeProcessModel(cleanedUpModels.processModel);
                setEventTypeSettingsModel(cleanedUpModels.settingsModel);
                setEventTypeScheduleModel(cleanedUpModels.scheduleModel);
                setEventTypeCostModel(cleanedUpModels.costModel);
                break;
            }
            case eventTypeSettingsStep: {
                setEventTypeSettingsModel(model);
                break;
            }
            case eventTypeScheduleStep: {
                setEventTypeScheduleModel(model);
                break;
            }
            case eventTypeCostStep: {
                setEventTypeCostModel(model);
                break;
            }
            case eventTypeChecksStep: {
                setEventTypeChecksModel(model);
                break;
            }
            default:
                break;
        }
    }

    function handleSave(stepName) {
        setSaving(true);

        const eventTypeRequestModel = EventTypeHelpers.getEventTypeRequestModel(
            null,
            eventTypeDetailsModel,
            eventTypeIconModel, 
            eventTypeProcessModel, 
            eventTypeSettingsModel, 
            eventTypeScheduleModel, 
            eventTypeCostModel,
            eventTypeChecksModel
        );
        
        FleetService.updateEventType(id, eventTypeRequestModel)
            .then(() => {
                NotificationToaster.show(Intent.SUCCESS, `The event type ${stepName} was updated successfully.`);
                loadEventType();
            })
            .catch((error) => {
                const apiError = error || `The event type ${stepName} could not be updated. Please try again later.`;

                NotificationToaster.show(Intent.WARNING, apiError);
            })
            .finally(() => {
                setSaving(false);
            });
    }

    function onForceLoadFinished() {
        setForceLoadChildren(false);
    }

    function getBreadcrumbs() {
        return [
            ...breadcrumbs,
            { title: `Edit ${currentEventType} Event Type` }
        ];
    }

    function hasAnyFailedOnlyOptionsSelected() {
        let internalHasAnyFailedOnlyOptionsSelected = false;
        const failStatusSelectedPages = []; 

        if (eventTypeProcessModel.requiresSignoffValue === FailedOnlySignoffOption.value) {
            internalHasAnyFailedOnlyOptionsSelected = true;
            failStatusSelectedPages.push("Process: event sign-off");
        }

        if (eventTypeSettingsModel.requireCommentValue === FailedOnlyCommentOption.value) {
            internalHasAnyFailedOnlyOptionsSelected = true;
            failStatusSelectedPages.push("Settings: comments");
        }

        if (eventTypeSettingsModel.requireCommentValue === FailedOnlyDefectOption.value) {
            internalHasAnyFailedOnlyOptionsSelected = true;
            failStatusSelectedPages.push("Settings: defects");
        }

        if (eventTypeSettingsModel.includeDocumentationFailedOnly === true) {
            internalHasAnyFailedOnlyOptionsSelected = true;
            failStatusSelectedPages.push("Settings: documentation");
        }

        if (eventTypeScheduleModel.vorTimerValue === FailedVorRequiredOption.value) {
            internalHasAnyFailedOnlyOptionsSelected = true;
            failStatusSelectedPages.push("Schedule: VOR");
        }

        if (eventTypeCostModel.requiresCostsValue === FailedOnlyCostOption.value) {
            internalHasAnyFailedOnlyOptionsSelected = true;
            failStatusSelectedPages.push("Costs");
        }

        return { hasAnyFailedOnlyOptionsSelected: internalHasAnyFailedOnlyOptionsSelected, failStatusSelectedPages: failStatusSelectedPages };
    }

    function hasChecksAttached() {
        return eventTypeChecksModel?.relatedTemplates !== null && eventTypeChecksModel?.relatedTemplates !== undefined && eventTypeChecksModel.relatedTemplates?.length > 0;
    }

    return (
        <UserAccess perform={requiredActions}
            yes={() => (
                <Fragment>
                    <div className="row">

                        <Breadcrumbs items={getBreadcrumbs()} />

                        <h1>{currentEventType} Event Type</h1>
                        <div className="intro-text">
                            <p>In this section you can configure your event type.</p>
                        </div>
                    </div>

                    <div className="layout-cols">
                        <div className="col side-col">
                            <div className="row">
                                <VerticalLinkGroup links={routes} exactRouteMatch={false}></VerticalLinkGroup>
                            </div>
                        </div>
                        <div className="col content-col">
                            <Routes>
                                <Route path="details" element={<AuthenticatedRoute action="FMS:Manage"><EventTypeDetails model={eventTypeDetailsModel} forceLoad={forceLoadChildren} isInWizardMode={false} saving={saving} globalLoading={loading} onModelUpdate={onModelUpdated} onSave={handleSave} onForceLoadFinished={onForceLoadFinished} hasChecksAttached={hasChecksAttached()} /></AuthenticatedRoute>} />
                                <Route path="icon" element={<AuthenticatedRoute action="FMS:Manage"><EventTypeIcon model={eventTypeIconModel} forceLoad={forceLoadChildren} isInWizardMode={false} saving={saving} globalLoading={loading} onModelUpdate={onModelUpdated} onSave={handleSave} onForceLoadFinished={onForceLoadFinished} /></AuthenticatedRoute>} />
                                <Route path="process" element={<AuthenticatedRoute action="FMS:Manage"><EventTypeProcess model={eventTypeProcessModel} forceLoad={forceLoadChildren} isInWizardMode={false} saving={saving} globalLoading={loading} onModelUpdate={onModelUpdated} onSave={handleSave} onForceLoadFinished={onForceLoadFinished} specialProcessingKey={specialProcessingKey} failStatusSettings={hasAnyFailedOnlyOptionsSelected()} erMaintenanceLocked={isErMaintenanceLocked} /></AuthenticatedRoute>} />
                                <Route path="settings" element={<AuthenticatedRoute action="FMS:Manage"><EventTypeSettings model={eventTypeSettingsModel} forceLoad={forceLoadChildren} processModel={eventTypeProcessModel} isInWizardMode={false} saving={saving} globalLoading={loading} onModelUpdate={onModelUpdated} onSave={handleSave} onForceLoadFinished={onForceLoadFinished} erMaintenanceLocked={isErMaintenanceLocked} /></AuthenticatedRoute>} />
                                <Route path="schedule" element={<AuthenticatedRoute action="FMS:Manage"><EventTypeSchedule model={eventTypeScheduleModel} forceLoad={forceLoadChildren} processModel={eventTypeProcessModel} isInWizardMode={false} saving={saving} globalLoading={loading} onModelUpdate={onModelUpdated} onSave={handleSave} onForceLoadFinished={onForceLoadFinished} specialProcessingKey={specialProcessingKey} /></AuthenticatedRoute>} />
                                <Route path="cost" element={<AuthenticatedRoute action="FMS:Manage"><EventTypeCost model={eventTypeCostModel} forceLoad={forceLoadChildren} processModel={eventTypeProcessModel} isInWizardMode={false} saving={saving} globalLoading={loading} onModelUpdate={onModelUpdated} onSave={handleSave} onForceLoadFinished={onForceLoadFinished} /></AuthenticatedRoute>} />
                                <Route path="checks" element={<AuthenticatedRoute action={["FMS:Manage", "Checks:Manage"]}><EventTypeChecks model={eventTypeChecksModel} forceLoad={forceLoadChildren} detailsModel={eventTypeDetailsModel} scheduleModel={eventTypeScheduleModel} isInWizardMode={false} saving={saving} globalLoading={loading} onModelUpdate={onModelUpdated} onSave={handleSave} onForceLoadFinished={onForceLoadFinished} /></AuthenticatedRoute>} />
                                <Route path="history" element={<AuthenticatedRoute action="FMS:Manage"><EventTypeChangelog /></AuthenticatedRoute>} />
                                <Route path="" element={<Navigate to={`/fleet-settings/event-types/${id}/details`} replace />} />
                            </Routes>
                        </div>


                    </div>
                </Fragment>
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}
